// src/features/customerModule/customers/customerExportConfig.js
//
// Single source of truth for what each customer tab exports.
//   - Tabs import their COLUMNS here → per-tab "Export CSV".
//   - customerWorkbook walks CUSTOMER_EXPORT_SECTIONS → all-tabs "Export Excel".
//
// Column keys below match the fields each tab actually renders.

import {
    fetchProfileRows,
    fetchDatasetRows,
    fetchNotesRows,
    fetchPaymentsRows,
    fetchHistoryRows,
    fetchTextsRows,
    fetchOptOutRows,
} from "./customerExportFetchers.js";

// Same normalisation TabProfile uses. Applied via `value` (not `format`) so the
// CSV and the Excel cell both get the formatted string.
export const formatPhone = (value) => {
    if (!value) return "";
    const digits = String(value).replace(/\D/g, "");
    const local = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    if (local.length !== 10) return value;
    return `(${local.slice(0, 3)})-${local.slice(3, 6)}-${local.slice(6)}`;
};

// ── Profile ────────────────────────────────────────────────────────
export const PROFILE_COLUMNS = [
    { key: "customerid",       label: "Customer ID" },
    { key: "customeruniqueid", label: "Customer Phone", value: (r) => formatPhone(r?.customeruniqueid) },
    { key: "firstname",        label: "First Name" },
    { key: "lastname",         label: "Last Name" },
    { key: "emailaddress",     label: "Email" },
    { key: "customerguid",     label: "Customer GUID" },
];

// ── Dataset ────────────────────────────────────────────────────────
// The tab groups by uploaddate and lays fields out three-across; flattened here
// to one row per uploaded record with the upload datetime leading.
export const DATASET_COLUMNS = [
    { key: "uploaddate",                 label: "Upload Date",                type: "datetime" },
    { key: "dayspastdue",                label: "Days Past Due",              type: "number" },
    { key: "totalamountdue",             label: "Total Amount Due",           type: "currency" },
    { key: "monthlypayment",             label: "Monthly Payment",            type: "currency" },
    { key: "extamt",                     label: "Extension Amount",           type: "currency" },
    { key: "exteligible",                label: "Extension Eligible" },
    { key: "rollamt",                    label: "Roll Amount",                type: "currency" },
    { key: "repoamt",                    label: "Repo Amount",                type: "currency" },
    { key: "repodate",                   label: "Repo Date",                  type: "datetime" },
    { key: "ccdigits",                   label: "Last 4 CC" },
    { key: "vehicleownamt",              label: "Vehicle Own Amount",         type: "currency" },
    { key: "no_payment_received_past",   label: "No Payment Received Past" },
    { key: "no_promise_received_past",   label: "No Promise Received Past" },
    { key: "most_recent_payment_date",   label: "Most Recent Payment Date",   type: "datetime" },
    { key: "most_recent_payment_amount", label: "Most Recent Payment Amount", type: "currency" },
    { key: "minimum_payment_amount",     label: "Minimum Payment Amount",     type: "currency" },
    { key: "minimum_promise_amount",     label: "Minimum Promise Amount",     type: "currency" },
];

// ── Notes ──────────────────────────────────────────────────────────
export const NOTES_COLUMNS = [
    { key: "notesvalue", label: "Note" },
    { key: "createdAt",  label: "Created At", type: "datetime" },
];

// ── Payments ───────────────────────────────────────────────────────
// Customer-scoped event rows from dbo.customerconvevent.
export const PAYMENTS_COLUMNS = [
    { key: "paymentDate",        label: "Date",           type: "datetime" },
    { key: "paymentType",        label: "Type" },
    { key: "paymentAmount",      label: "Amount",         type: "currency" },
    { key: "paymentFeeAmount",   label: "Fee",            type: "currency" },
    { key: "totalAmount",        label: "Total",          type: "currency" },
    { key: "confirmationNumber", label: "Confirmation #" },
    { key: "channel",            label: "Channel" },
];

// ── History ────────────────────────────────────────────────────────
export const HISTORY_COLUMNS = [
    { key: "Channel",    label: "Channel" },
    { key: "Who",        label: "Sender" },
    { key: "message",    label: "Message" },
    { key: "event_time", label: "Created At", type: "datetime" },
];

// ── Texts ──────────────────────────────────────────────────────────
// Flattened out of the per-conversation accordions; conversation id leads so
// the sheet can be grouped/filtered on it.
export const TEXTS_COLUMNS = [
    { key: "ConversationID15",  label: "Conversation ID" },
    { key: "userresponse",      label: "Customer" },
    { key: "cararesponse",      label: "Response" },
    { key: "userresponsetime",  label: "Date",          type: "datetime" },
    { key: "cararesponsetime",  label: "Response Date", type: "datetime" },
];

// ── OptOut ─────────────────────────────────────────────────────────
export const OPTOUT_COLUMNS = [
    { key: "Channel",      label: "Channel" },
    { key: "ContactValue", label: "Contact" },
    { key: "Date",         label: "Opt-Out Date", type: "datetime" },
];

// ── Sections (workbook sheet order) ────────────────────────────────
// `sheet` is the Excel tab name — "History" alone is reserved by Excel, so the
// History section ships as "Message History". `name` is the CSV filename segment
// and is unaffected.
export const CUSTOMER_EXPORT_SECTIONS = [
    { key: "Profile",  name: "profile",  sheet: "Profile",         columns: PROFILE_COLUMNS,  fetch: fetchProfileRows },
    { key: "Dataset",  name: "dataset",  sheet: "Dataset",         columns: DATASET_COLUMNS,  fetch: fetchDatasetRows },
    { key: "Notes",    name: "notes",    sheet: "Notes",           columns: NOTES_COLUMNS,    fetch: fetchNotesRows },
    { key: "Payments", name: "payments", sheet: "Payments",        columns: PAYMENTS_COLUMNS, fetch: fetchPaymentsRows },
    { key: "History",  name: "history",  sheet: "Message History", columns: HISTORY_COLUMNS,  fetch: fetchHistoryRows },
    { key: "Texts",    name: "texts",    sheet: "Texts",           columns: TEXTS_COLUMNS,    fetch: fetchTextsRows },
    { key: "OptOut",   name: "optout",   sheet: "OptOut",          columns: OPTOUT_COLUMNS,   fetch: fetchOptOutRows },
];

export const getExportSection = (key) =>
    CUSTOMER_EXPORT_SECTIONS.find((s) => s.key === key) || null;